import React, { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { UploadCloud, Check, X, Image, Camera, Film } from "lucide-react";
import { Button } from "@/components/ui/button";
import FadeIn from "./FadeIn";
import { useIsMobile } from "@/hooks/use-mobile";

interface UploadZoneProps {
  onFileSelected: (file: File) => void;
  className?: string;
  maxFileSizeMB?: number;
  allowedFileTypes?: string[];
  mediaFile?: File | null;
  onMediaProcessed?: () => void;
}

const UploadZone: React.FC<UploadZoneProps> = ({
  onFileSelected,
  className,
  maxFileSizeMB = 10,
  allowedFileTypes = ["image/jpeg", "image/png", "image/webp", "video/mp4", "video/quicktime", "video/webm"],
  mediaFile = null,
  onMediaProcessed,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const validateFile = (file: File): boolean => {
    if (!allowedFileTypes.includes(file.type)) {
      setError(`File type not supported. Please upload an image (JPG, PNG, WebP) or video (MP4, MOV, WebM)`);
      return false;
    }

    if (file.size > maxFileSizeMB * 1024 * 1024) {
      setError(`File too large. Maximum size is ${maxFileSizeMB}MB.`);
      return false;
    }

    setError(null);
    return true;
  };

  const processFile = (file: File) => {
    if (!validateFile(file)) return;

    // Check if file is a video
    setIsVideo(file.type.startsWith("video/"));

    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
      setSuccess(true);
    };
    reader.readAsDataURL(file);

    onFileSelected(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const handleOpenCamera = () => {
    // On mobile, we can use the capture attribute
    if (isMobile) {
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    } else {
      // On desktop, we need to use the MediaDevices API
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // Create a video element to show the camera stream
        const videoElement = document.createElement("video");
        const canvasElement = document.createElement("canvas");
        const stream = document.createElement("div");

        stream.style.position = "fixed";
        stream.style.top = "0";
        stream.style.left = "0";
        stream.style.width = "100%";
        stream.style.height = "100%";
        stream.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
        stream.style.zIndex = "9999";
        stream.style.display = "flex";
        stream.style.flexDirection = "column";
        stream.style.alignItems = "center";
        stream.style.justifyContent = "center";

        videoElement.style.maxWidth = "100%";
        videoElement.style.maxHeight = "80vh";
        videoElement.style.objectFit = "contain";
        videoElement.autoplay = true;

        const controls = document.createElement("div");
        controls.style.marginTop = "20px";
        controls.style.display = "flex";
        controls.style.gap = "10px";

        const captureBtn = document.createElement("button");
        captureBtn.textContent = "Capture Photo";
        captureBtn.style.padding = "10px 20px";
        captureBtn.style.backgroundColor = "var(--primary)";
        captureBtn.style.color = "white";
        captureBtn.style.border = "none";
        captureBtn.style.borderRadius = "4px";
        captureBtn.style.cursor = "pointer";

        const cancelBtn = document.createElement("button");
        cancelBtn.textContent = "Cancel";
        cancelBtn.style.padding = "10px 20px";
        cancelBtn.style.backgroundColor = "#f44336";
        cancelBtn.style.color = "white";
        cancelBtn.style.border = "none";
        cancelBtn.style.borderRadius = "4px";
        cancelBtn.style.cursor = "pointer";

        controls.appendChild(captureBtn);
        controls.appendChild(cancelBtn);

        stream.appendChild(videoElement);
        stream.appendChild(controls);

        document.body.appendChild(stream);

        let mediaStream: MediaStream | null = null;

        navigator.mediaDevices
          .getUserMedia({ video: { facingMode: "environment" } })
          .then((stream) => {
            mediaStream = stream;
            videoElement.srcObject = stream;
          })
          .catch((error) => {
            console.error("Camera access error:", error);
            setError("Could not access camera. Please check your permissions.");
            document.body.removeChild(stream);
          });

        const cleanup = () => {
          if (mediaStream) {
            mediaStream.getTracks().forEach((track) => track.stop());
          }
          if (document.body.contains(stream)) {
            document.body.removeChild(stream);
          }
        };

        cancelBtn.addEventListener("click", cleanup);

        captureBtn.addEventListener("click", () => {
          // Set canvas dimensions to match the video
          canvasElement.width = videoElement.videoWidth;
          canvasElement.height = videoElement.videoHeight;

          // Draw the current video frame to the canvas
          const context = canvasElement.getContext("2d");
          if (context) {
            context.drawImage(videoElement, 0, 0, canvasElement.width, canvasElement.height);

            // Convert the canvas to a data URL, then to a Blob
            canvasElement.toBlob(
              (blob) => {
                if (blob) {
                  // Create a File object from the Blob
                  const file = new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });

                  // Process the file
                  processFile(file);

                  // Cleanup
                  cleanup();
                }
              },
              "image/jpeg",
              0.95
            );
          }
        });
      } else {
        setError("Camera access is not supported in this browser. Try using a mobile device or a different browser.");
      }
    }
  };

  const handleOpenVideoCamera = () => {
    if (isMobile) {
      if (videoInputRef.current) {
        videoInputRef.current.click();
      }
    } else {
      // For desktop, we can use the same MediaDevices API but for video recording
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const videoElement = document.createElement("video");
        const stream = document.createElement("div");

        stream.style.position = "fixed";
        stream.style.top = "0";
        stream.style.left = "0";
        stream.style.width = "100%";
        stream.style.height = "100%";
        stream.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
        stream.style.zIndex = "9999";
        stream.style.display = "flex";
        stream.style.flexDirection = "column";
        stream.style.alignItems = "center";
        stream.style.justifyContent = "center";

        videoElement.style.maxWidth = "100%";
        videoElement.style.maxHeight = "70vh";
        videoElement.style.objectFit = "contain";
        videoElement.autoplay = true;
        videoElement.muted = true;

        const controls = document.createElement("div");
        controls.style.marginTop = "20px";
        controls.style.display = "flex";
        controls.style.gap = "10px";

        const startBtn = document.createElement("button");
        startBtn.textContent = "Start Recording";
        startBtn.style.padding = "10px 20px";
        startBtn.style.backgroundColor = "var(--primary)";
        startBtn.style.color = "white";
        startBtn.style.border = "none";
        startBtn.style.borderRadius = "4px";
        startBtn.style.cursor = "pointer";

        const stopBtn = document.createElement("button");
        stopBtn.textContent = "Stop Recording";
        stopBtn.style.padding = "10px 20px";
        stopBtn.style.backgroundColor = "#f44336";
        stopBtn.style.color = "white";
        stopBtn.style.border = "none";
        stopBtn.style.borderRadius = "4px";
        stopBtn.style.cursor = "pointer";
        stopBtn.style.display = "none";

        const cancelBtn = document.createElement("button");
        cancelBtn.textContent = "Cancel";
        cancelBtn.style.padding = "10px 20px";
        cancelBtn.style.backgroundColor = "#757575";
        cancelBtn.style.color = "white";
        cancelBtn.style.border = "none";
        cancelBtn.style.borderRadius = "4px";
        cancelBtn.style.cursor = "pointer";

        const recordingIndicator = document.createElement("div");
        recordingIndicator.style.marginBottom = "10px";
        recordingIndicator.style.color = "white";
        recordingIndicator.style.display = "none";

        controls.appendChild(startBtn);
        controls.appendChild(stopBtn);
        controls.appendChild(cancelBtn);

        stream.appendChild(recordingIndicator);
        stream.appendChild(videoElement);
        stream.appendChild(controls);

        document.body.appendChild(stream);

        let mediaStream: MediaStream | null = null;
        let mediaRecorder: MediaRecorder | null = null;
        let recordedChunks: Blob[] = [];
        let recordingTimer: number | null = null;
        let recordingTime = 0;

        navigator.mediaDevices
          .getUserMedia({ video: true, audio: true })
          .then((stream) => {
            mediaStream = stream;
            videoElement.srcObject = stream;
          })
          .catch((error) => {
            console.error("Camera access error:", error);
            setError("Could not access camera. Please check your permissions.");
            document.body.removeChild(stream);
          });

        const cleanup = () => {
          if (mediaStream) {
            mediaStream.getTracks().forEach((track) => track.stop());
          }
          if (recordingTimer) {
            clearInterval(recordingTimer);
          }
          if (document.body.contains(stream)) {
            document.body.removeChild(stream);
          }
        };

        cancelBtn.addEventListener("click", cleanup);

        startBtn.addEventListener("click", () => {
          if (mediaStream) {
            mediaRecorder = new MediaRecorder(mediaStream);
            recordedChunks = [];

            mediaRecorder.ondataavailable = (e) => {
              if (e.data.size > 0) {
                recordedChunks.push(e.data);
              }
            };

            mediaRecorder.onstop = () => {
              const recordedBlob = new Blob(recordedChunks, { type: "video/webm" });
              const file = new File([recordedBlob], `video_${Date.now()}.webm`, { type: "video/webm" });
              processFile(file);
              cleanup();
            };

            // Start recording
            mediaRecorder.start(100);
            startBtn.style.display = "none";
            stopBtn.style.display = "block";
            recordingIndicator.style.display = "block";

            // Show recording time
            recordingTime = 0;
            recordingTimer = window.setInterval(() => {
              recordingTime += 1;
              const minutes = Math.floor(recordingTime / 60);
              const seconds = recordingTime % 60;
              recordingIndicator.textContent = `Recording: ${minutes.toString().padStart(2, "0")}:${seconds
                .toString()
                .padStart(2, "0")}`;

              // Auto-stop after 30 seconds
              if (recordingTime >= 30 && mediaRecorder) {
                mediaRecorder.stop();
                clearInterval(recordingTimer as number);
              }
            }, 1000);
          }
        });

        stopBtn.addEventListener("click", () => {
          if (mediaRecorder && mediaRecorder.state !== "inactive") {
            mediaRecorder.stop();
            if (recordingTimer) {
              clearInterval(recordingTimer);
            }
          }
        });
      } else {
        setError("Video recording is not supported in this browser. Try using a mobile device or a different browser.");
      }
    }
  };

  const handleReset = () => {
    setPreview(null);
    setError(null);
    setSuccess(false);
    setIsVideo(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
    if (videoInputRef.current) {
      videoInputRef.current.value = "";
    }
  };

  // Add effect to handle external media file
  React.useEffect(() => {
    if (mediaFile) {
      processFile(mediaFile);
      onMediaProcessed?.(); // Call the callback if provided
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFile]); // Only run when mediaFile changes

  return (
    <div className={cn("", className)}>
      {preview ? (
        <div className="relative overflow-hidden rounded-lg border bg-background">
          {isVideo ? (
            <video src={preview} controls className="w-full h-auto max-h-[200px] object-contain" />
          ) : (
            <img src={preview} alt="Preview" className="w-full h-auto max-h-[200px] object-contain" />
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity">
            <div className="flex gap-2">
              <Button variant="destructive" size="sm" onClick={handleReset} className="border-2 border-white/20">
                <X className="w-4 h-4 mr-2" /> Remove
              </Button>
            </div>
          </div>
          <div className="absolute top-4 right-4">
            <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-800 border border-green-200">
              <Check className="w-3 h-3" /> Uploaded
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "relative rounded-lg border-2 border-dashed p-6 transition-all text-center cursor-pointer",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50",
              error && "border-red-500/50 bg-red-50"
            )}
          >
            <FadeIn className="flex flex-col items-center gap-2">
              {error ? (
                <X className="w-8 h-8 text-red-500" />
              ) : (
                <div className="relative">
                  {isDragging && (
                    <UploadCloud className="w-8 h-8 text-primary animate-bounce" />
                  )}
                </div>
              )}

              <div className="space-y-1">
                <h3 className="text-sm font-medium">
                  {error ? "Upload Error" : "Upload your product photo or video"}
                </h3>
                <p className="text-xs text-muted-foreground max-w-[260px] mx-auto">
                  {error ? (
                    <span className="text-red-500">{error}</span>
                  ) : isDragging ? (
                    "Drop your file here"
                  ) : (
                    "Drag and drop or click to browse"
                  )}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {!error && `Supports JPG, PNG, WebP, MP4, MOV up to ${maxFileSizeMB}MB`}
                </p>
              </div>
            </FadeIn>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept={allowedFileTypes.join(",")}
              className="hidden"
            />
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center">
              <span className="px-2 text-[11px] text-muted-foreground">OR</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="flex items-center justify-center gap-1.5"
              onClick={handleOpenCamera}
            >
              <Camera className="w-3.5 h-3.5" />
              Take a Photo
            </Button>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="flex items-center justify-center gap-1.5"
              onClick={handleOpenVideoCamera}
            >
              <Film className="w-3.5 h-3.5" />
              Take a Video
            </Button>
          </div>

          <input
            type="file"
            ref={cameraInputRef}
            onChange={handleFileChange}
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
          />

          <input
            type="file"
            ref={videoInputRef}
            onChange={handleFileChange}
            accept="video/mp4,video/quicktime,video/webm"
            capture="environment"
            className="hidden"
          />
        </div>
      )}
    </div>
  );
};

export default UploadZone;
